<script setup lang="ts">
  import { computed, useAttrs } from 'vue';

  import { StashPrimaryColorGroup } from '../../../types/colors';
  import Chip from '../Chip/Chip.vue';

  defineOptions({
    name: 'll-license-chip',
    inheritAttrs: false,
  });

  export interface LicenseChipProps {
    /**
     * License type to be displayed. This can be either the type code (e.g. "MED") or the full name (e.g. "Medical").
     */
    license: string;

    /**
     * Size of the chip. Defaults to medium. Available sizes: "small" | "medium".
     */
    size?: 'small' | 'medium';

    /**
     * Whether the chip is disabled. Defaults to false.
     */
    disabled?: boolean;
  }

  const props = withDefaults(defineProps<LicenseChipProps>(), {
    size: 'medium',
  });
  const attrs = useAttrs();
  const chipAttrs = computed(() => {
    const tempAttrs = { ...attrs };

    // prevents override to ensure license chips remain consistent
    delete tempAttrs['color-scheme'];
    delete tempAttrs['radius'];
    delete tempAttrs['shade'];

    return tempAttrs;
  });

  const COLOR_SCHEME_MAPPING = Object.freeze({
    MED: 'yellow',
    MEDI: 'yellow',
    REC: 'blue',
    RECR: 'blue',
    COM: 'royal',
    COMB: 'royal',
    ANC: 'orange',
    ANCI: 'orange',
    BUL: 'ice',
    BULK: 'ice',
    CAN: 'green',
    CANN: 'green',
    ADU: 'blue',
    ADUL: 'blue',
  });

  const color = computed<StashPrimaryColorGroup>(() => {
    if (props.disabled) {
      return 'ice';
    }

    if (formattedLicense.value in COLOR_SCHEME_MAPPING) {
      return COLOR_SCHEME_MAPPING[formattedLicense.value];
    }

    return 'blue';
  });

  const formattedLicense = computed(() => {
    const uppercaseLicense = props.license.toUpperCase();

    if (uppercaseLicense.length > 4) {
      return uppercaseLicense.slice(0, 3);
    }

    return uppercaseLicense;
  });
</script>

<template>
  <Chip
    v-show="formattedLicense.length"
    class="stash-license-chip"
    data-test="stash-license-chip"
    radius="pill"
    shade="main"
    :color-scheme="color"
    :size="props.size"
    v-bind="chipAttrs"
    :disabled="disabled"
  >
    {{ formattedLicense }}
  </Chip>
</template>
